import React from "react";
import ParticleBackground from "../components/ParticleBackground";
import {
  FaSignInAlt,
  FaBookReader,
  FaSyncAlt,
  FaDownload,
  FaClock,
  FaFileAlt,
  FaUpload,
  FaCheckCircle,
  FaArrowLeft,
  FaDesktop,
  FaExclamationTriangle,
  FaHeadset,
  FaGraduationCap,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const ExamAttemptGuide = () => {
  const attemptSteps = [
    {
      step: "01",
      icon: <FaSignInAlt className="text-primary" />,
      title: "Open Your LMS Portal",
      desc: "Open the Hunarmand Punjab LMS portal and log in to your student account.",
    },
    {
      step: "02",
      icon: <FaBookReader className="text-success" />,
      title: "Select Your Exam Course",
      desc: "Check the date sheet and open the course for which you want to take the final exam.",
    },
    {
      step: "03",
      icon: <FaSyncAlt className="text-info" />,
      title: "Find the Final Exam Section",
      desc: 'At the top of that course, you will see "FINAL EXAM QUESTION PAPER". It will become active at 7:00 PM on the scheduled exam day. If the question paper is not visible after 7:00 PM, refresh your LMS portal tab to display it.',
    },
    {
      step: "04",
      icon: <FaDownload className="text-warning" />,
      title: "Download and Read the Paper",
      desc: "Open the exam section, download the question paper and carefully read all paper instructions before starting.",
    },
    {
      step: "05",
      icon: <FaClock className="text-danger" />,
      title: "Solve Within the Given Time",
      desc: "Complete your paper within the allotted exam time. Prepare your answers in the required format (PDF, Word or ZIP), exactly as instructed in the paper.",
    },
    {
      step: "06",
      icon: <FaFileAlt className="text-secondary" />,
      title: "Upload Your Solved Paper",
      desc: 'Below the question paper, find "Assignment Upload". Click "Choose File", select your completed paper from your device and upload it.',
    },
    {
      step: "07",
      icon: <FaUpload className="text-success" />,
      title: "Submit Your Solved Paper",
      desc: 'After uploading the correct file, click the "Submit Assignment" button to submit your final exam paper.',
    },
    {
      step: "08",
      icon: <FaCheckCircle className="text-success" />,
      title: "Confirm Submission",
      desc: 'Once submitted successfully, the status "Under Review" will appear. This means your paper has been submitted for review.',
    },
  ];

  return (
    <div className="exam-attempt-guide-page" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Banner */}
      <div
        className="breadcrums position-relative text-white py-5"
        style={{
          background: "linear-gradient(135deg, #0d3b24 0%, #065f46 50%, #047857 100%)",
          overflow: "hidden",
        }}
      >
        <ParticleBackground />
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="text-center py-4">
            <span
              className="badge px-3 py-2 mb-3 text-uppercase font-12 font-md-14 text-wrap"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(6px)",
                letterSpacing: "1px",
                borderRadius: "30px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                maxWidth: "100%",
                display: "inline-block",
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              <FaGraduationCap className="me-2" />
              Hunarmand Punjab Final Exams
            </span>
            <h1 className="fw-bold fs-2 fs-md-1 mb-2 text-white">
              How to Attempt and Submit Your Final Exam Paper
            </h1>
            <p className="lead mb-0 text-white-50 font-16 font-md-18">
              START TIME: 7:00 PM (PKT) | Follow your selected course’s exam date and time on the official date sheet.
            </p>
          </div>
        </div>
      </div>

      <div className="container py-3 py-md-5">
        {/* Navigation Back Link */}
        <div className="mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2">
          <Link
            to="/online-exam"
            className="btn btn-outline-dark px-3 py-2 fw-semibold d-inline-flex align-items-center justify-content-center text-center"
            style={{ borderRadius: "8px", fontSize: "14px" }}
          >
            <FaArrowLeft className="me-2" /> Back to Online Exam Rules
          </Link>
          <div className="d-flex flex-column flex-sm-row gap-2">
            <Link
              to="/exam-datesheet"
              className="btn btn-outline-success px-3 py-2 fw-semibold d-inline-flex align-items-center justify-content-center text-center"
              style={{ borderRadius: "8px", fontSize: "14px" }}
            >
              View Date Sheet
            </Link>
            <a
              href="https://lms.hunarmandpunjab.org.pk/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary px-3 py-2 fw-bold d-inline-flex align-items-center justify-content-center text-center shadow-sm"
              style={{ borderRadius: "8px", backgroundColor: "#1e40af", borderColor: "#1e40af", fontSize: "14px" }}
            >
              <FaDesktop className="me-2" /> Open LMS Portal
            </a>
          </div>
        </div>

        {/* Main Content Box */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", background: "#ffffff" }}>
          <div
            className="p-4 p-md-5 text-white"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 60%, #3b82f6 100%)",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <span
              className="badge px-3 py-1 mb-2 text-uppercase font-12"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              Step-by-Step Instructions
            </span>
            <h2 className="fw-bold mb-2 text-white font-28">
              Complete Submission Workflow
            </h2>
            <p className="text-white-50 mb-0 font-15" style={{ lineHeight: "1.7" }}>
              Follow the steps below carefully to download, complete and submit your final examination through the LMS portal.
            </p>
          </div>

          <div className="card-body p-3 p-md-5">
            {/* 8 Steps Grid - Responsive */}
            <div className="row g-3 g-md-4">
              {attemptSteps.map((stepItem, sIdx) => (
                <div className="col-12 col-md-6 col-lg-3 d-flex" key={sIdx}>
                  <div
                    className="p-3 p-md-4 rounded-4 w-100 d-flex flex-column shadow-sm bg-white"
                    style={{
                      border: "1px solid #e2e8f0",
                      borderTop: "4px solid #1e40af",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold font-24" style={{ color: "#1e40af" }}>
                        {stepItem.step}
                      </span>
                      <div
                        className="p-2 rounded-circle bg-light d-flex align-items-center justify-content-center"
                        style={{ width: "38px", height: "38px", fontSize: "16px" }}
                      >
                        {stepItem.icon}
                      </div>
                    </div>
                    <h5 className="fw-bold text-dark mb-2 font-16">{stepItem.title}</h5>
                    <p className="text-secondary font-13 mb-0 flex-grow-1" style={{ lineHeight: "1.7" }}>
                      {stepItem.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* IMPORTANT REMINDERS BOX */}
            <div
              className="mt-4 mt-md-5 p-3 p-md-4 rounded-4"
              style={{
                backgroundColor: "#fffbeb",
                border: "2px dashed #f59e0b",
              }}
            >
              <h4 className="fw-bold text-dark mb-3 d-flex align-items-center font-18">
                <FaExclamationTriangle className="text-warning me-2" />
                IMPORTANT REMINDERS
              </h4>
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <div className="bg-white p-3 rounded-3 h-100 border shadow-sm">
                    <strong className="text-danger d-block mb-1 font-14">
                      <FaCheckCircle className="me-1 text-danger" /> ONE SUBJECT ONLY
                    </strong>
                    <p className="font-13 text-secondary mb-0" style={{ lineHeight: "1.6" }}>
                      Students may attempt the final exam for one subject only; attempting two subject exams is not allowed.
                    </p>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="bg-white p-3 rounded-3 h-100 border shadow-sm">
                    <strong className="text-success d-block mb-1 font-14">
                      <FaClock className="me-1 text-success" /> BE READY ON TIME
                    </strong>
                    <p className="font-13 text-secondary mb-0" style={{ lineHeight: "1.6" }}>
                      Note your chosen subject’s exam date and time, log in to your LMS portal on schedule, and submit within the allotted period.
                    </p>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="bg-white p-3 rounded-3 h-100 border shadow-sm">
                    <strong className="text-warning d-block mb-1 font-14">
                      <FaClock className="me-1 text-warning" /> FINAL DEADLINE &amp; 5-MINUTE GRACE PERIOD
                    </strong>
                    <p className="font-13 text-secondary mb-0" style={{ lineHeight: "1.6" }}>
                      Submit your paper before the exam time ends. If needed, you have 5 extra minutes to finish uploading and submitting. After this 5-minute period, the “FINAL EXAM QUESTION PAPER” page will automatically close on your LMS portal. You cannot submit your paper once the page closes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="mt-4 text-center pt-3 border-top">
              <a
                href="https://lms.hunarmandpunjab.org.pk/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success fw-bold px-4 py-2 me-2 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                Go to LMS Portal
              </a>
              <a
                href="https://complaint.hunarmandpunjab.org.pk/login"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary fw-semibold px-4 py-2"
                style={{ borderRadius: "8px" }}
              >
                <FaHeadset className="me-2" /> Complaint Portal
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamAttemptGuide;
